import React, {useEffect, useState} from "react";
import {Skeleton, Grid, Box, Typography} from "@mui/material";
import {BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from "recharts";
import DateRangePickerComponent from "../../common/CustomDateRangePicker";
import moment from "moment";
import useDashboard from "../../../hooks/useDashboard";
import EmptyIcon from "../../../assets/images/empty-box.png";
import useDashboardResetFilterStore from "../../../zustand/useDashboardResetFilterStore";


const AuthenticationCountComponent = (props) => {
    const {breakpoints} = props;

    const {isDashboardResetFilters, setDashboardResetFilters} = useDashboardResetFilterStore();
    const {authenticationCount, getDashboardAuthenticationCount, isFetchingDashboardAuthenticationCount} = useDashboard();

    const [startDate, setStartDate] = useState(moment().subtract(6, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [authenticationCountData, setAuthenticationCountData] = useState([]);


    useEffect(() => {
        if (isDashboardResetFilters) {
            setStartDate(moment().subtract(6, "days").format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
        }
    }, [isDashboardResetFilters]);

    useEffect(() => {
        if (startDate && endDate) { 
            getDashboardAuthenticationCount(startDate, endDate);
            setDashboardResetFilters(false);
        }
    }, [startDate, endDate])

    useEffect(() => {
        if (authenticationCount) {
            const aggregateDataByMonth = (authenticationCount) => {
                const aggregatedData = {};
            
                const processEntry = (entry) => {
                    const monthYear = moment(entry.date).format("MMMM YYYY");
            
                    if (!aggregatedData[monthYear]) {
                        aggregatedData[monthYear] = {date: monthYear, totalCount: 0};
                    }
            
                    aggregatedData[monthYear].totalCount += entry.count;
                }
            
                authenticationCount?.product?.forEach(processEntry);
                authenticationCount?.document?.forEach(processEntry);
            
                return Object.values(aggregatedData);
            }
                        
            setAuthenticationCountData(aggregateDataByMonth(authenticationCount));
        }
    }, [authenticationCount])


    const handleDateRangeChange = (startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }
    

    return (
        <Box sx={{backgroundColor: "#fff", mt: "20px", padding: "24px", pt: "24px", borderRadius: "8px"}}> 
            <Grid container>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography sx={{color: "#58565f", fontSize: 18, mb: 3, fontWeight: 600}}>
                        Authentication Count
                    </Typography>
                </Grid>

                <Grid item xs={6} sm={6} md={6} lg={6} xl={6} sx={{textAlign: "end", justifyContent: "end", ml: "auto", display: "flex"}}>   
                    <DateRangePickerComponent 
                        initialStartDate={startDate}
                        initialEndDate={endDate}
                        ranges={{
                            "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                            "This Month": [moment().startOf('month'), moment().endOf('month')],
                            "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                            "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                        }}
                        onDateRangeChange={handleDateRangeChange}
                    />
                </Grid>
            </Grid>

            {isFetchingDashboardAuthenticationCount ?
                <Skeleton variant="rounded" sx={{backgroundColor: "#d8d8fdbd"}} height={495} />
            :
                (!isFetchingDashboardAuthenticationCount && authenticationCountData?.length === 0) ?
                    <Box sx={{height: 495, textAlign: "center", flexDirection: "column", 
                        justifyContent: "center", display: "flex", alignItems: "center", 
                    }}>
                        <img alt="empty-data-icon" src={EmptyIcon} width="200" style={{marginTop: "-84px"}} />

                        <Typography sx={{color: "#58565f", fontSize: 14, mt: 1}}>
                            No data available for the selected date range
                        </Typography>
                    </Box>
            :
                <ResponsiveContainer width="100%" height={495} style={{paddingTop: "12px"}}>
                    <BarChart width={500} height={400} data={authenticationCountData}
                        margin={{top: 5, right: 30, left: 20, bottom: 5}}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />

                        <Tooltip />
                        <Legend />

                        <Bar dataKey="totalCount" fill="#7A78F7" name="count" activeBar={<Rectangle fill="#7A78F7" stroke="#7A78F7" />} />
                    </BarChart>
                </ResponsiveContainer>
            }
        </Box>
    )
}

export default AuthenticationCountComponent